import React from 'react';

import { Typography } from '@material-ui/core';
import { get } from 'lodash';
import { ILumaBotFlowQuestion } from 'ui-model';

import { LumaBotContextProps } from '../LumaBot.context';
import { ELumaBotQuestionType, EMessageType } from '../WIP/ui-model/types';

import Message, { MessageProps } from './Message';
import MessageAppointments from './MessageAppointments';
import MessageBookStatus from './MessageBookStatus';
import MessageMultipleChoice from './MessageMultipleChoice';
import MessageScheduler from './MessageScheduler';

export type TMessageWrapper = MessageProps['message'] & {
  metadata: {
    item?: ILumaBotFlowQuestion;
    flowTemplateId?: string;
    flowId?: string;
    patient?: string;
    answer?: any;
    includeRevert?: boolean;
    isLastMessage?: boolean;
    forceInput?: boolean;
    action?: 'verifyPatient' | 'scheduleAppointment';
    hideAvatar?: boolean;
    hideName?: boolean;
    nextMessage?: any;
    prevMessage?: any;
  };
};

export interface MessageWrapperProps extends Omit<MessageProps, 'classes'> {
  message: TMessageWrapper;
  onSubmit: LumaBotContextProps['pushAnswer'];
}

const MessageWrapper: React.FC<MessageWrapperProps> = ({ message, variant, onSubmit, onRevert, ...rest }) => {
  const {
    metadata: { item, prevMessage, nextMessage },
  } = message;
  const itemType = item?.type;
  const isInbound = message.type === EMessageType.Inbound;

  if (isInbound) {
    if (
      itemType === ELumaBotQuestionType.MultipleChoice ||
      itemType === ELumaBotQuestionType.YesNo ||
      itemType === ELumaBotQuestionType.TwoFA
    ) {
      return (
        <MessageMultipleChoice
          nextMessage={nextMessage}
          prevMessage={prevMessage}
          variant={variant}
          message={message}
          onSubmit={onSubmit}
          {...rest}
        />
      );
    }

    if (itemType === ELumaBotQuestionType.AvailabilityDetails) {
      return <MessageBookStatus flowId={message.lumabotFlowId} />;
    }

    if (itemType === ELumaBotQuestionType.ManageAppointment) {
      return (
        <MessageAppointments
          nextMessage={nextMessage}
          prevMessage={prevMessage}
          variant={variant}
          message={message}
          {...rest}
        />
      );
    }
  }

  const template = get(message, 'template') || get(item, 'properties.template');

  if (itemType === ELumaBotQuestionType.Scheduler) {
    return (
      <MessageScheduler
        nextMessage={nextMessage}
        prevMessage={prevMessage}
        message={message}
        onSubmit={onSubmit}
        messageTemplate={template as string}
        variant={variant}
        onRevert={onRevert}
        {...rest}
      />
    );
  }

  return (
    <Message
      nextMessage={nextMessage}
      prevMessage={prevMessage}
      variant={variant}
      message={message}
      onRevert={onRevert}
      {...rest}
    >
      {template && <Typography dangerouslySetInnerHTML={{ __html: template }} />}
    </Message>
  );
};

export default MessageWrapper;
